import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MetricDisplay } from '../components/LocationTender/MetricDisplay';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MapPin, LogIn, ChevronDown, Footprints } from 'lucide-react';

interface LocationTenderData {
  id: string;
  venueName: string;
  venueType?: string;
  slug: string;
  description?: string;
  location?: string;
  startingPriceCents?: number;
  metrics?: any;
  heroImages?: string[];
  machineIds?: string[];
  Machines?: Array<{
    id: string;
    name: string;
    costCents: number;
    location?: string;
    venueName?: string | null;
    footfall?: number | null;
    priceFromPer2WeeksCents?: number | null;
    MachineMedia?: Array<{
      id: string;
      kind: string;
      url: string;
      sortOrder: number;
    }>;
  }>;
}

export default function LocationTenderPitch() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<LocationTenderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [machinesExpanded, setMachinesExpanded] = useState(true);
  const [selectedMachineForModal, setSelectedMachineForModal] = useState<NonNullable<LocationTenderData['Machines']>[number] | null>(null);

  useEffect(() => {
    if (!slug) {
      setError('Invalid location tender link');
      setLoading(false);
      return;
    }

    fetch(`/api/store/newlocation/${slug}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
      },
      cache: 'no-store',
    })
      .then(async (res) => {
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({ error: 'Unknown error' }));
          if (res.status === 404) {
            throw new Error('Location tender not found');
          }
          throw new Error(errorData.error || 'Failed to load location tender');
        }
        return res.json();
      })
      .then((data) => {
        setData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching location tender:', err);
        setError(err.message || 'Failed to load location tender');
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return null;
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md shadow-sm">
          <CardHeader>
            <CardTitle className="text-foreground font-semibold">Location Not Found</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">{error || 'The location tender you are looking for does not exist.'}</p>
            <Button onClick={() => navigate('/login')} className="w-full">
              <LogIn className="w-4 h-4 mr-2" />
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const safeMetrics = data.metrics && typeof data.metrics === 'object' ? data.metrics : {};

  // Check if we have any metrics to display
  const hasMetrics = safeMetrics && Object.keys(safeMetrics).length > 0;
  const hasEnabledMetrics = safeMetrics?.enabledMetrics && Array.isArray(safeMetrics.enabledMetrics) && safeMetrics.enabledMetrics.length > 0;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Compact Header */}
      <div className="bg-card border-b sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <h1 className="flex items-center gap-3 text-foreground font-semibold text-xl md:text-2xl tracking-tight">
              <span className="h-8 w-1 rounded-full bg-brand-primary" aria-hidden />
              {data.venueName}
            </h1>
            <Button onClick={() => navigate('/login')} size="sm" className="bg-brand-primary hover:bg-brand-primary/90">
              <LogIn className="w-4 h-4 mr-2" />
              Login
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        {/* Hero Images - Compact */}
        {data.heroImages && data.heroImages.length > 0 && (
          <div className="mb-8">
            <div className={`grid gap-3 ${data.heroImages.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
              {data.heroImages.map((img, idx) => (
                <div key={idx} className="overflow-hidden rounded-lg">
                  <img
                    src={img}
                    alt={`${data.venueName} ${idx + 1}`}
                    className="w-full h-auto object-cover max-h-[300px]"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Location Section */}
        {data.location && (
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/80">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span className="text-foreground font-medium text-sm">{data.location}</span>
            </div>
          </div>
        )}

        {/* Metrics Section */}
        {hasEnabledMetrics && (
          <div className="mb-10">
            <h2 className="text-foreground font-semibold text-lg mb-4">Overview</h2>
            <MetricDisplay metrics={safeMetrics} />
          </div>
        )}

        {/* Machines Section - Collapsible */}
        {data.Machines && data.Machines.length > 0 && (
          <div className="mb-10">
            <Collapsible open={machinesExpanded} onOpenChange={setMachinesExpanded}>
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-3 mb-4 w-full text-left hover:opacity-80 transition-opacity">
                  <ChevronDown 
                    className={`h-5 w-5 text-muted-foreground transition-transform ${machinesExpanded ? 'rotate-0' : '-rotate-90'}`}
                  />
                  <h2 className="text-foreground font-semibold text-lg">Campaign Machines</h2>
                  <Badge variant="secondary" className="text-xs">{data.Machines.length}</Badge>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {data.Machines.map((machine) => {
                    const machineImage = machine.MachineMedia?.find(m => m.kind === 'render') || machine.MachineMedia?.[0];
                    const pricePer2WeeksCents = machine.priceFromPer2WeeksCents ?? (machine.costCents * 14);
                    const priceFormatted = (pricePer2WeeksCents / 100).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
                    const footfallFormatted = machine.footfall != null ? machine.footfall.toLocaleString() : null;
                    return (
                      <Card key={machine.id} className="overflow-hidden shadow-sm hover:shadow transition-shadow">
                        {machineImage && (
                          <div className="aspect-video overflow-hidden bg-muted">
                            <img
                              src={machineImage.url}
                              alt={machine.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <CardHeader className="pb-3">
                          <CardTitle className="text-foreground font-medium text-sm">{machine.name}</CardTitle>
                          {machine.location && (
                            <p className="text-xs text-muted-foreground mt-1">{machine.location}</p>
                          )}
                        </CardHeader>
                        <CardContent className="space-y-3 pt-0">
                          <div className="flex items-baseline gap-2">
                            <span className="text-lg font-semibold text-foreground">From £{priceFormatted}</span>
                            <span className="text-xs text-muted-foreground">/ 2 weeks</span>
                          </div>
                          {footfallFormatted && (
                            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                              <Footprints className="w-4 h-4" />
                              <span>{footfallFormatted} / 2 weeks</span>
                            </div>
                          )}
                          {machine.venueName && (
                            <Badge variant="secondary" className="text-xs">{machine.venueName}</Badge>
                          )}
                          <Button
                            size="sm"
                            className="w-full bg-brand-primary hover:bg-brand-primary/90 text-xs"
                            onClick={() => setSelectedMachineForModal(machine)}
                          >
                            View Now
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </div>

      {/* Machine Detail Modal */}
      <Dialog open={!!selectedMachineForModal} onOpenChange={(open) => !open && setSelectedMachineForModal(null)}>
        <DialogContent className="max-w-md shadow-lg">
          {selectedMachineForModal && (
            <>
              <DialogHeader>
                <DialogTitle className="text-foreground font-semibold">{selectedMachineForModal.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {(() => {
                  const img = selectedMachineForModal.MachineMedia?.find(m => m.kind === 'render') || selectedMachineForModal.MachineMedia?.[0];
                  const priceCents = selectedMachineForModal.priceFromPer2WeeksCents ?? (selectedMachineForModal.costCents * 14);
                  const priceStr = (priceCents / 100).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
                  return (
                    <>
                      {img && (
                        <div className="aspect-video overflow-hidden rounded-lg bg-muted">
                          <img src={img.url} alt={selectedMachineForModal.name} className="w-full h-full object-cover" />
                        </div>
                      )}
                      {selectedMachineForModal.location && (
                        <p className="text-sm text-muted-foreground">{selectedMachineForModal.location}</p>
                      )}
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg font-semibold text-foreground">From £{priceStr}</span>
                        <span className="text-sm text-muted-foreground">/ 2 weeks</span>
                      </div>
                      {selectedMachineForModal.footfall != null && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Footprints className="w-4 h-4" />
                          <span>{selectedMachineForModal.footfall.toLocaleString()} / 2 weeks</span>
                        </div>
                      )}
                      {selectedMachineForModal.venueName && (
                        <Badge variant="secondary">{selectedMachineForModal.venueName}</Badge>
                      )}
                      <Button
                        className="w-full bg-brand-primary hover:bg-brand-primary/90"
                        onClick={() => navigate('/login')}
                      >
                        <LogIn className="w-4 h-4 mr-2" />
                        Login to Get Started
                      </Button>
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Compact Footer CTA */}
      <div className="bg-muted/50 border-t mt-10">
        <div className="container mx-auto px-4 py-8 text-center">
          <p className="text-foreground font-medium mb-4">Sign in to view full details and add machines to your campaign</p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-brand-primary hover:bg-brand-primary/90"
          >
            <LogIn className="w-4 h-4 mr-2" />
            Login to Get Started
          </Button>
        </div>
      </div>
    </div>
  );
}
